import { FunctionComponent, useMemo } from 'react'
import { Colors } from '@pubstack/ui/style/colors'
import { Chip } from '@pubstack/ui/components/Chip'
import { Link } from '@pubstack/ui/components/Link'
import { FlyoutBody, FlyoutTitle, Section } from '@pubstack/ui/components/GlobalFlyoutWrapper'
import { AdQualityRule } from '@pubstack/common/src/ad-quality-rule'
import { IAB_CATEGORY_MAP } from '@pubstack/common/src/pbs/pbs-ad-quality-config'
import { useSites, useSitesByIds } from '~/api/app/sites.api'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import { collapseBlockedCategories } from './EditAdQualityFlyout'

const TRACKING_LABEL = 'settings-ad-quality-view'
type Props = {
  rule: AdQualityRule
}

const openList = (list: string[]) => {
  const content = list.join('\n')
  const blob = new Blob([content], { type: 'text/plain; charset=utf-8' })
  const url = URL.createObjectURL(blob)
  window.open(url, '_blank')
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

export const ViewAdQualityFlyout: FunctionComponent<Props> = ({ rule }) => {
  const user = useAuthenticatedUser()
  const { data: sites, isLoading: isLoadingSites } = useSites(user.currentScopeId)

  const missingSiteIds = useMemo(() => (isLoadingSites ? [] : (rule.siteIds ?? []).filter((siteId) => !sites.some((site) => site.id === siteId))), [isLoadingSites, rule.siteIds, sites])
  const { data: missingSites } = useSitesByIds(missingSiteIds, user.currentScopeId)

  const siteNamesById = useMemo(() => Object.fromEntries([...sites, ...missingSites].map((site) => [site.id, site.name])), [sites, missingSites])

  const displayedCategories = useMemo(() => {
    const collapsed = collapseBlockedCategories(rule.blockedCategories)
    return collapsed.map((code) => IAB_CATEGORY_MAP[code] ?? code)
  }, [rule.blockedCategories])

  return (
    <>
      <FlyoutTitle> {rule.name} - details</FlyoutTitle>
      <FlyoutBody>
        {rule.siteIds && rule.siteIds.length > 0 && (
          <Section>
            <p className="my-1 text-slate-grey">Media</p>
            <div className="inline-flex flex-wrap items-center content-center gap-1 w-full">
              {rule.siteIds.map((siteId) => (
                <Chip key={`scope-${siteId}`} text={siteNamesById[siteId] ?? siteId} borderColor={Colors.Platinum} color={Colors.Ghost} />
              ))}
            </div>
          </Section>
        )}

        {rule.blockedCategories && rule.blockedCategories.length > 0 && (
          <Section>
            <p className="my-1 text-slate-grey">IAB Categories</p>
            <div className="inline-flex flex-wrap items-center content-center gap-1 w-full">
              {displayedCategories.map((category, index) => (
                <Chip key={`category-${index}`} text={category} borderColor={Colors.Platinum} color={Colors.Ghost} />
              ))}
            </div>
          </Section>
        )}

        {rule.blockedAdvertiserDomains && rule.blockedAdvertiserDomains.length > 0 && (
          <Section>
            <div>
              <p className="my-1 text-slate-grey">Domains blocklist</p>
              <span>{rule.blockedAdvertiserDomains.length}</span>
            </div>
            <Link
              icon={'open_in_new'}
              target={'_blank'}
              rel={'noreferrer'}
              label={'View URLs'}
              onClick={() => openList(rule.blockedAdvertiserDomains ?? [])}
              trackingLabel={`${TRACKING_LABEL}-urls-view`}
            />
          </Section>
        )}
      </FlyoutBody>
    </>
  )
}
